import { useCallback } from "react";

import { Button, Modal, Space, Tooltip, Typography } from "antd";
import { clsx } from "clsx";
import capitalize from "lodash/capitalize";
import { NavigateFunction } from "react-router-dom";

import {
    RVReadMore,
    RVStatusBadge,
    BADGE_STATUS,
    RVGridActionableCell,
    RVTimeDisplay,
    getFilterValue,
    getSortOrder,
    OperatorType,
    QueryParams,
    RVGRID_COLUMN_SIZE_OPTIONS,
} from "@regal-voice/ui";
import HighlightCustomVariables from "Components/shared/HighlightCustomVariables/HighlightCustomVariables";
import {
    CommunicationTemplateState,
    CommunicationTemplateChannelType,
    CommunicationTemplate,
    CommunicationTemplateChannel,
} from "Types/CommunicationTemplate";
import { GridColumnsType } from "Types/ComponentTypes/RVGridTypes";
import { Team } from "Types/Team";

import styles from "./CommunicationTemplates.module.scss";

interface DeleteCommunicationTemplateButtonProps {
    templateId: string;
    templateName: string;
    loading: boolean;
    action: (id: string) => void;
}

const channelFilters = ["sms", "email"].map((status) => ({ text: capitalize(status), value: status }));

function DeleteCommunicationTemplateButton({
    templateId,
    templateName,
    action,
    loading,
}: DeleteCommunicationTemplateButtonProps): JSX.Element {
    const confirmCommunicationTemplateDelete = useCallback(() => {
        Modal.confirm({
            title: "Delete Template",
            content: `Are you sure you want to delete the template "${templateName}"?`,
            cancelText: "Cancel",
            okText: "Yes, Delete Template",
            okType: "danger",
            onOk: () => {
                action(templateId);
            },
        });
    }, [templateId, templateName, action]);

    return (
        <Button danger onClick={confirmCommunicationTemplateDelete} loading={loading}>
            Delete
        </Button>
    );
}

export function getColumns(
    navigate: NavigateFunction,
    queryParams: Partial<QueryParams>,
    isDeleting: boolean,
    removeCommunicationTemplate: (id: string) => void
): GridColumnsType<CommunicationTemplate> {
    return [
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.ID,
            dataIndex: "friendlyId",
            key: "friendlyId",
            title: "ID #",
            // eslint-disable-next-line react/display-name
            render(friendlyId: string, record: CommunicationTemplate) {
                return <RVGridActionableCell content={friendlyId} linkTo={record.id} />;
            },
            sorter: true,
            sortOrder: getSortOrder(queryParams, "friendlyId", null),
        },
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.MEDIUM,
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: true,
            sortOrder: getSortOrder(queryParams, "name", null),
            // eslint-disable-next-line react/display-name
            render(name: string, record: CommunicationTemplate) {
                return <RVGridActionableCell content={name} linkTo={record.id} tooltip={record.description} />;
            },
        },

        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.XSMALL,
            title: "Channel",
            dataIndex: "channel",
            key: "channel",
            filters: channelFilters,
            filteredValue: getFilterValue(queryParams, "channel"),
            filterMultiple: true,
            filterOperator: OperatorType.IN,
            render(value: CommunicationTemplateChannelType) {
                return (
                    <span className={clsx(styles.channel, value === CommunicationTemplateChannel.sms && styles.sms)}>
                        {value}
                    </span>
                );
            },
        },

        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.LARGE,
            title: "Preview",
            dataIndex: "preview",
            key: "preview",
            render(_: any, record) {
                return (
                    <Typography.Paragraph className={styles.preview} ellipsis={{ rows: 2 }}>
                        <HighlightCustomVariables text={record.body} isPlainText={record.channel === "sms"} />
                    </Typography.Paragraph>
                );
            },
        },
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.XXSMALL,
            title: "State",
            dataIndex: "state",
            key: "state",
            sorter: true,
            sortOrder: getSortOrder(queryParams, "state", null),
            filters: ["live", "draft"].map((status) => ({ text: capitalize(status), value: status })),
            filteredValue: getFilterValue(queryParams, "state"),
            filterMultiple: true,
            filterOperator: OperatorType.IN,
            render(state: CommunicationTemplateState) {
                const status = state === CommunicationTemplateState.DRAFT ? BADGE_STATUS.PENDING : BADGE_STATUS.ACTIVE;
                return <RVStatusBadge status={status} content={state} />;
            },
        },
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.SMALL,
            dataIndex: "teams",
            key: "teams",
            title: "Visibility",
            // eslint-disable-next-line react/display-name
            render: (teams: Array<Team>) => {
                return (
                    <>
                        {teams?.length ? (
                            <Tooltip placement="bottom" title={teams.map((t) => t.name).join(", ")}>
                                <RVReadMore key={"teams"} rows={2} className={styles.teamsVisibilityNoMargin}>
                                    {teams.map((t) => t.name).join(", ")}
                                </RVReadMore>
                            </Tooltip>
                        ) : (
                            "All Users"
                        )}
                    </>
                );
            },
        },
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.SMALL,
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            // eslint-disable-next-line react/display-name
            render: (_: any, record: Record<string, any>): JSX.Element => {
                return (
                    <div className={styles.timeDisplayWrapper}>
                        <span>
                            <RVTimeDisplay time={record.createdAt} />
                        </span>
                        <span className={styles.userName}>{record.createdBy}</span>
                    </div>
                );
            },
        },
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.SMALL,
            title: "Updated",
            dataIndex: "updatedAt",
            key: "updatedAt",
            // eslint-disable-next-line react/display-name
            render: (_: any, record: Record<string, any>): JSX.Element => {
                return (
                    <div className={styles.timeDisplayWrapper}>
                        <RVTimeDisplay time={record.updatedAt} />
                        <span className={styles.userName}>{record.updatedBy}</span>
                    </div>
                );
            },
        },
        {
            key: "actions",
            dataIndex: "actions",
            title: "Actions",
            size: RVGRID_COLUMN_SIZE_OPTIONS.SMALL,
            render(_: any, template) {
                return (
                    <Space size={16} wrap align="baseline" key={template.id}>
                        <Button onClick={() => navigate(template.id)}>Edit</Button>

                        <DeleteCommunicationTemplateButton
                            templateId={template.id}
                            templateName={template.name}
                            action={removeCommunicationTemplate}
                            loading={isDeleting}
                        />
                    </Space>
                );
            },
        },
    ];
}
